import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import ButtonWithLoadingState from "@/features/Common/components/ButtonWithLoadingState";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import {
  VoucherOrderStatus,
  VoucherPaymentReceipt,
} from "@/services/core-api-adapter";
import { defaultFullscreenPageStyling, panelStyle } from "@/theme";
import {
  convertPxToRem,
  formatAmount,
  formatDateToReadableString,
  getShortDayFromDate,
} from "@/utils";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface VoucherOrderSummaryProps {
  voucherPaymentReceipt: VoucherPaymentReceipt;
  onViewReceiptClick: () => void;
  onBackButtonClick: () => void;
  onShareVoucherClick: () => void;
  onSaveToProfileClick: () => void;
  isDownloadingVoucherFile: boolean;
}

export default function VoucherOrderSummary({
  voucherPaymentReceipt,
  onViewReceiptClick,
  onBackButtonClick,
  onShareVoucherClick,
  onSaveToProfileClick,
  isDownloadingVoucherFile,
}: VoucherOrderSummaryProps) {
  const { t } = useTranslation();
  const [voucherType, setVoucherType] = useState<string>("");

  useEffect(() => {
    if (voucherPaymentReceipt?.voucherSubTypeName === "Doctor") {
      setVoucherType("doctorVoucher");
    } else if (voucherPaymentReceipt?.voucherSubTypeName === "Nurse") {
      setVoucherType("nurseVoucher");
    } else {
      setVoucherType("bloodTestVoucher");
    }
  }, [voucherPaymentReceipt]);

  const shouldShowSaveToProfileOrShareButton =
    voucherPaymentReceipt?.status === VoucherOrderStatus.PAYMENT_SUCCEEDED;

  return (
    <Stack sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel={true} onClick={onBackButtonClick} />
      <Stack justifyContent="center" alignItems="center" pt={5}>
        <Typography variant="h1">{t("OrderSummary.title")}</Typography>
        <Chip
          icon={<IconLoader icon="CloudStatusIcon" />}
          label={t("OrderSummary.pillText")}
          sx={{ borderRadius: 2, paddingTop: 1 }}
        />
        <Typography variant="body1" pt={1}>
          {t("OrderSummary.subtitle")}
        </Typography>
        <Typography variant="body1" pb={1}>
          {voucherPaymentReceipt?.voucherCode}
        </Typography>

        <Stack
          direction="row"
          gap={1}
          pt={1}
          alignItems="center"
          justifyContent="center"
        >
          <Chip
            icon={<IconLoader color="success" icon="CheckInCircleFilledIcon" />}
            label={t("OrderSummary.chipText")}
            sx={{
              borderRadius: 2,
              color: "green",
              borderWidth: 1,
              borderColor: "black",
            }}
          />

          <Typography variant="body1">
            {voucherPaymentReceipt?.paymentUtcDateTime
              ? getShortDayFromDate(
                  new Date(voucherPaymentReceipt.paymentUtcDateTime)
                )
              : ""}
            ,{" "}
            {voucherPaymentReceipt?.paymentUtcDateTime
              ? formatDateToReadableString(
                  new Date(voucherPaymentReceipt.paymentUtcDateTime)
                )
              : ""}
          </Typography>
        </Stack>

        <Stack
          spacing={2}
          padding={2}
          pt={5}
          width="100%"
          sx={{ ...panelStyle, marginTop: 2 }}
        >
          <Typography variant="h3" fontWeight={600}>
            {t(`OrderSummary.PanelOne.${voucherType}.title` as any)}
          </Typography>
          <Divider sx={{ width: "100%" }} />
          <Typography>{t("OrderSummary.PanelOne.description")}</Typography>

          {shouldShowSaveToProfileOrShareButton && (
            <>
              {isDownloadingVoucherFile ? (
                <ButtonWithLoadingState size="small" spinnerSize={18} />
              ) : (
                <ButtonWithAnalytics
                  page="VoucherOrderSummary"
                  text={t("OrderSummary.PanelOne.shareButton")}
                  intent="navigational"
                  onClick={onShareVoucherClick}
                  size="small"
                >
                  {t("OrderSummary.PanelOne.shareButton")}
                </ButtonWithAnalytics>
              )}
            </>
          )}

          {shouldShowSaveToProfileOrShareButton && (
            <ButtonWithAnalytics
              page="VoucherOrderSummary"
              text={t("OrderSummary.PanelOne.saveButton")}
              intent="navigational"
              onClick={onSaveToProfileClick}
              size="small"
              variant="outlined"
            >
              {t("OrderSummary.PanelOne.saveButton")}
            </ButtonWithAnalytics>
          )}
        </Stack>
        {voucherPaymentReceipt.showReceipt === true && (
          <Stack
            spacing={2}
            padding={2}
            pt={5}
            width="100%"
            sx={{ ...panelStyle, marginTop: 2 }}
          >
            <Typography variant="h3" fontWeight={600}>
              {t("OrderSummary.PanelTwo.title")}
            </Typography>
            <Divider />
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
            >
              <Stack>
                <Typography variant="h4" fontWeight={400}>
                  {t(`OrderSummary.PanelOne.${voucherType}.title` as any)}
                </Typography>
                {voucherPaymentReceipt.isDiscountApplied && (
                  <Typography
                    color="neutral.light"
                    variant="body2"
                    sx={{ textDecoration: "line-through" }}
                  >
                    {formatAmount(voucherPaymentReceipt?.originalAmount, "ZAR")}
                  </Typography>
                )}
              </Stack>
              <Typography variant="h4" color="primary">
                {formatAmount(voucherPaymentReceipt?.paymentAmount, "ZAR")}
              </Typography>
            </Box>
            <Divider sx={{ width: "100%" }} />
            <Stack direction={"row"} justifyContent="space-between">
              <Typography variant="h4">
                {t("OrderSummary.PanelTwo.totalText")}
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontSize: convertPxToRem(26) }}
                color="primary"
              >
                {formatAmount(voucherPaymentReceipt?.paymentAmount, "ZAR")}
              </Typography>
            </Stack>

            <Divider sx={{ width: "100%" }} />
            <ButtonWithAnalytics
              page="VoucherOrderSummary"
              text={t("OrderSummary.bottomButton")}
              intent="navigational"
              onClick={onViewReceiptClick}
              size="small"
            >
              {t("OrderSummary.bottomButton")}
            </ButtonWithAnalytics>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
